import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ChevronRightIcon,
  ClipboardDocumentListIcon,
  CheckBadgeIcon,
  CircleStackIcon,
} from '@heroicons/react/24/outline';
import { useEmpresas } from '../hooks/usePesquisas';
import { usePermissoes } from '../hooks/usePermissoes';
import { useToast } from '../components/ui/Toast';
import { useConfirmDialog } from '../components/ui/ConfirmDialog';
import {
  useMetaCompraDimensaoLote,
  useCriarMetasCompraDimensaoLote,
} from '../hooks/useMetaCompraDimensaoLote';
import InputMoeda from '../components/ui/InputMoeda';
import { money } from '../utils/format';
import AjudaTitulo from '../components/ui/AjudaTitulo';
import { btnPrimario, btnSecundario, inputIris } from '../components/ui/iris';
import type { DimensaoTipo } from '../types/metaCompraDimensao';

const STORAGE_EMPRESAS = 'metas.dim.lote.empresas';
const STORAGE_ANOMES = 'metas.dim.lote.anoMes';
const STORAGE_DIMENSAO = 'metas.dim.lote.dimensao';

function anoMesAtual(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function nomeMes(anoMes: string): string {
  if (!/^\d{4}-\d{2}$/.test(anoMes)) return anoMes;
  const [ano, m] = anoMes.split('-').map(Number);
  const nomes = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
                 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
  return `${nomes[m - 1]} / ${ano}`;
}

/** Estado editável de cada linha da tabela. */
interface LinhaState {
  selecionada: boolean;
  valorMeta: number | null;
  descricao: string;
}

/**
 * Loading rico pra grid de lote — query pode levar 5-30s em bases grandes;
 * sem feedback decente o operador acha que travou.
 *
 * Combina:
 *  - Pulso visual no card central (CircleStackIcon + spinner)
 *  - Contador de segundos decorridos
 *  - Mensagem que vai escalando: "Buscando" → "Calculando 6M" → "Base maior, segure"
 *  - 8 linhas de skeleton pulsantes pra dar contorno da tabela final
 */
const LoadingGrid: React.FC<{ dimensao: string; limit: number }> = ({ dimensao, limit }) => {
  const [segundos, setSegundos] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setSegundos((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, []);

  let mensagem: string;
  let tom: string;
  if (segundos < 3) {
    mensagem = `Buscando top ${limit} ${dimensao}…`;
    tom = 'text-accent-text';
  } else if (segundos < 10) {
    mensagem = 'Calculando histórico dos últimos 6 meses…';
    tom = 'text-accent-text';
  } else if (segundos < 30) {
    mensagem = 'Processando base — pode levar mais alguns segundos…';
    tom = 'text-warning';
  } else {
    mensagem = 'Base grande — aguarde, está quase saindo…';
    tom = 'text-warning';
  }

  return (
    <div className="flex h-full flex-col">
      {/* Card central com spinner e mensagem progressiva */}
      <div className="flex flex-col items-center justify-center gap-3 py-8">
        <div className="relative">
          <div className="absolute inset-0 animate-ping rounded-full bg-accent-soft" />
          <div className="relative flex h-12 w-12 items-center justify-center rounded-full bg-accent-soft">
            <CircleStackIcon className="h-6 w-6 text-accent-text" />
          </div>
        </div>
        <div className="text-center">
          <p className={`text-sm font-medium ${tom}`}>{mensagem}</p>
          <p className="mt-0.5 text-[11px] text-tx-3">
            {segundos}s decorrido{segundos === 1 ? '' : 's'}
          </p>
        </div>
      </div>

      {/* Skeleton da tabela — dá sensação de "espaço já reservado, só esperando dados" */}
      <div className="flex-1 px-3 pb-3">
        <div className="space-y-1.5">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="flex items-center gap-2 rounded-iris-sm border border-line bg-surface-2 p-2">
              <div className="h-3 w-3 animate-pulse rounded bg-surface-2" />
              <div className="h-3 w-20 animate-pulse rounded bg-surface-2" />
              <div className="h-3 flex-1 animate-pulse rounded bg-surface-2" style={{ animationDelay: `${i * 80}ms` }} />
              <div className="h-3 w-16 animate-pulse rounded bg-surface-2" />
              <div className="h-3 w-16 animate-pulse rounded bg-surface-2" />
              <div className="h-3 w-12 animate-pulse rounded bg-surface-2" />
              <div className="h-6 w-24 animate-pulse rounded bg-surface-2" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const MetasCompraDimensaoLotePage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirmDialog();
  const { temPermissao, isAdmin } = usePermissoes();
  // `compras.lote` — a MESMA chave que o backend exige no POST /metas/dimensao/lote
  // (policy "compras.lote"). Ficou `compras.aprovar` por um tempo, que é nome de
  // POLICY, não chave de permissão: `temPermissao` compara contra o catálogo de
  // CHAVES, então a expressão era sempre falsa e o rodapé de salvar só aparecia
  // pra ADMIN — o comprador com tudo marcado montava o lote e não tinha como gravar.
  const podeEditar = isAdmin || temPermissao('compras.lote');

  const { data: empresas = [] } = useEmpresas();

  const [empresasSel, setEmpresasSel] = useState<string[]>(() => {
    try { const raw = localStorage.getItem(STORAGE_EMPRESAS); return raw ? JSON.parse(raw) : []; } catch { return []; }
  });
  const [anoMes, setAnoMes] = useState<string>(() => localStorage.getItem(STORAGE_ANOMES) ?? anoMesAtual());
  const [dimensao, setDimensao] = useState<DimensaoTipo>(() => {
    const v = localStorage.getItem(STORAGE_DIMENSAO);
    return (v === 'fornecedor' || v === 'marca' || v === 'grupo') ? v : 'fornecedor';
  });
  const [limit, setLimit] = useState(15);

  useEffect(() => { localStorage.setItem(STORAGE_EMPRESAS, JSON.stringify(empresasSel)); }, [empresasSel]);
  useEffect(() => { localStorage.setItem(STORAGE_ANOMES, anoMes); }, [anoMes]);
  useEffect(() => { localStorage.setItem(STORAGE_DIMENSAO, dimensao); }, [dimensao]);

  // Estado editável por linha (chave = código). Reset ao mudar filtros.
  const [linhas, setLinhas] = useState<Record<string, LinhaState>>({});

  const toggleEmpresa = (cod: string) => {
    setEmpresasSel((prev) => prev.includes(cod) ? prev.filter((e) => e !== cod) : [...prev, cod]);
  };

  const { data, isLoading, isFetching, refetch } = useMetaCompraDimensaoLote(
    { anoMes, empresas: empresasSel, dimensao, limit },
    empresasSel.length > 0,
  );
  const criarLoteMutation = useCriarMetasCompraDimensaoLote();

  // Quando data muda, popula linhas com defaults — preserva edições do operador
  // pra linhas que continuam existindo.
  useEffect(() => {
    if (!data) return;
    setLinhas((prev) => {
      const next: Record<string, LinhaState> = {};
      for (const item of data.items) {
        const existente = prev[item.codigo];
        next[item.codigo] = existente ?? {
          selecionada: false,
          valorMeta: item.sugestaoValor > 0 ? item.sugestaoValor : null,
          descricao: `Meta ${item.nome.slice(0, 80)}`,
        };
      }
      return next;
    });
  }, [data]);

  const itensSelecionados = useMemo(() => {
    if (!data) return [];
    return data.items.filter((it) => linhas[it.codigo]?.selecionada);
  }, [data, linhas]);

  /** Agregados sobre o conjunto SELECIONADO — atualizam em tempo real. */
  const agregados = useMemo(() => {
    const somaMetas = itensSelecionados.reduce((acc, it) => acc + (linhas[it.codigo]?.valorMeta ?? 0), 0);
    const somaCompra3M = itensSelecionados.reduce((acc, it) => acc + it.compra3M, 0);
    // Crescimento ponderado pelo volume de compra (mais justo que média aritmética
    // — uma marca pequena com +200% não pode distorcer o agregado).
    let crescPonderado = 0;
    if (somaCompra3M > 0) {
      const numer = itensSelecionados.reduce(
        (acc, it) => acc + it.crescimentoVendasPct * it.compra3M, 0,
      );
      crescPonderado = numer / somaCompra3M;
    }
    return { somaMetas, somaCompra3M, crescPonderado };
  }, [itensSelecionados, linhas]);

  // Compat: alguns lugares usam o nome antigo. Mantém só pro footer/confirm.
  const somaSelecionada = agregados.somaMetas;

  // ── Distribuidor: operador define meta total, sistema rateia proporcional ──
  const [metaTotal, setMetaTotal] = useState<number | null>(null);

  const distribuirMetaTotal = () => {
    if (!data || metaTotal == null || metaTotal <= 0) {
      toast.erro('Informe a meta total a distribuir.');
      return;
    }
    if (itensSelecionados.length === 0) {
      toast.erro('Selecione ao menos 1 item pra distribuir.');
      return;
    }
    if (agregados.somaCompra3M <= 0) {
      toast.erro('Itens selecionados não têm volume de compra 3M — rateio impossível.');
      return;
    }
    // Rateio proporcional ao volume de compra 3M. Último item absorve a sobra
    // de centavos do arredondamento pra fechar o total exato.
    setLinhas((prev) => {
      const next = { ...prev };
      let acumulado = 0;
      for (let i = 0; i < itensSelecionados.length; i++) {
        const it = itensSelecionados[i];
        const ultimo = i === itensSelecionados.length - 1;
        const valor = ultimo
          ? Math.max(0, Math.round((metaTotal - acumulado) * 100) / 100)
          : Math.round((metaTotal * (it.compra3M / agregados.somaCompra3M)) * 100) / 100;
        acumulado += valor;
        next[it.codigo] = { ...next[it.codigo], valorMeta: valor };
      }
      return next;
    });
    toast.sucesso(`Distribuído ${money(metaTotal)} entre ${itensSelecionados.length} item(s).`);
  };

  const updateLinha = (codigo: string, patch: Partial<LinhaState>) => {
    setLinhas((prev) => ({ ...prev, [codigo]: { ...prev[codigo], ...patch } }));
  };

  const toggleTodos = () => {
    if (!data) return;
    const elegives = data.items.filter((it) => !it.jaTemMeta);
    const algumNaoSel = elegives.some((it) => !linhas[it.codigo]?.selecionada);
    setLinhas((prev) => {
      const next = { ...prev };
      for (const it of elegives) {
        next[it.codigo] = { ...next[it.codigo], selecionada: algumNaoSel };
      }
      return next;
    });
  };

  const aplicarSugestaoTodos = () => {
    if (!data) return;
    setLinhas((prev) => {
      const next = { ...prev };
      for (const it of data.items) {
        next[it.codigo] = { ...next[it.codigo], valorMeta: it.sugestaoValor };
      }
      return next;
    });
  };

  const salvar = async () => {
    if (itensSelecionados.length === 0) {
      toast.erro('Selecione ao menos 1 item.');
      return;
    }
    // Valida valores
    const invalidos = itensSelecionados.filter((it) => {
      const v = linhas[it.codigo]?.valorMeta;
      return v == null || v <= 0;
    });
    if (invalidos.length > 0) {
      toast.erro(`${invalidos.length} item(s) com valor inválido.`);
      return;
    }

    const ok = await confirm({
      titulo: `Criar ${itensSelecionados.length} metas?`,
      mensagem: `Soma das metas: ${money(somaSelecionada)}. Mês: ${nomeMes(anoMes)}. Empresas: ${empresasSel.join(', ')}.`,
      textoConfirmar: 'Criar metas',
    });
    if (!ok) return;

    try {
      const resp = await criarLoteMutation.mutateAsync({
        anoMes,
        dimensao,
        empresas: empresasSel,
        items: itensSelecionados.map((it) => ({
          codigoDimensao: it.codigo,
          nomeDimensao: it.nome,
          descricao: linhas[it.codigo].descricao.trim() || `Meta ${it.nome}`.slice(0, 120),
          valorMeta: linhas[it.codigo].valorMeta!,
        })),
      });
      if (resp.totalErro === 0) {
        toast.sucesso(`${resp.totalSucesso} meta(s) criada(s).`);
      } else {
        const erros = resp.resultados.filter((r) => !r.sucesso);
        const primeiroErro = erros[0]?.erro ?? 'Erro desconhecido';
        toast.erro(`${resp.totalSucesso} criada(s) · ${resp.totalErro} falha(s). 1ª falha: ${primeiroErro}`);
      }
      // Limpa seleção dos que foram criados com sucesso
      setLinhas((prev) => {
        const next = { ...prev };
        for (const r of resp.resultados.filter((x) => x.sucesso)) {
          if (next[r.codigoDimensao]) {
            next[r.codigoDimensao] = { ...next[r.codigoDimensao], selecionada: false };
          }
        }
        return next;
      });
      // Re-busca pra atualizar status "já tem meta"
      refetch();
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } } };
      toast.erro(e.response?.data?.message ?? 'Falha ao criar metas em lote.');
    }
  };

  const items = data?.items ?? [];
  const elegives = items.filter((it) => !it.jaTemMeta).length;
  const todosElegSel = elegives > 0 && items.filter((it) => !it.jaTemMeta).every((it) => linhas[it.codigo]?.selecionada);

  return (
    <div className="mx-auto flex h-full w-full max-w-[1280px] flex-col gap-3">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <Link
            to="/compras/metas/dimensao"
            className="rounded p-1 text-tx-2 hover:bg-surface-2 hover:text-tx"
            aria-label="Voltar"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <h1 className="flex items-center gap-2 text-[22px] font-bold text-tx tracking-[-0.02em]">
            <ClipboardDocumentListIcon className="h-6 w-6 flex-shrink-0 text-accent-text" aria-hidden />
            Cadastrar metas em lote
            <AjudaTitulo>
              Sugestões baseadas nos <b>últimos 3 meses fechados</b>, ranqueadas por volume (itens em
              alta destacados). Escolha mês alvo/dimensão na barra abaixo, ajuste os valores e
              confirme pra criar várias metas de uma vez.
            </AjudaTitulo>
          </h1>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="flex h-8 items-center gap-1 rounded-iris border border-line px-2.5 text-xs text-tx-2 hover:bg-surface-2 transition-colors disabled:opacity-50"
        >
          <ArrowPathIcon className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} />
          Atualizar
        </button>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-end gap-3 rounded-iris-lg border border-line bg-surface p-3 shadow-sm">
        <div className="flex flex-col">
          <label className="mb-1 text-[10px] uppercase tracking-wide text-tx-3">Mês alvo</label>
          <input
            type="month"
            value={anoMes}
            onChange={(e) => setAnoMes(e.target.value)}
            className={`${inputIris} h-8 text-sm`}
          />
        </div>
        <div className="flex flex-col">
          <label className="mb-1 text-[10px] uppercase tracking-wide text-tx-3">Tipo de dimensão</label>
          <div className="flex gap-1">
            {(['fornecedor', 'marca', 'grupo'] as DimensaoTipo[]).map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setDimensao(d)}
                className={`h-8 rounded-iris-sm border px-3 text-xs font-medium capitalize transition-colors ${
                  dimensao === d
                    ? 'border-tx bg-accent text-white'
                    : 'border-line bg-surface text-tx-2 hover:bg-surface-2'
                }`}
              >
                {d}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-col">
          <label className="mb-1 text-[10px] uppercase tracking-wide text-tx-3">Top</label>
          <select
            value={limit}
            onChange={(e) => setLimit(Number.parseInt(e.target.value, 10))}
            className={`${inputIris} h-8 text-sm`}
          >
            <option value={10}>Top 10</option>
            <option value={15}>Top 15</option>
            <option value={30}>Top 30</option>
            <option value={50}>Top 50</option>
            <option value={100}>Top 100</option>
          </select>
        </div>
        <div className="flex-1 min-w-[240px]">
          <label className="mb-1 block text-[10px] uppercase tracking-wide text-tx-3">Empresas</label>
          <div className="flex flex-wrap gap-1">
            {empresas.map((e) => {
              const ativa = empresasSel.includes(e.codigo);
              return (
                <button
                  key={e.codigo}
                  type="button"
                  onClick={() => toggleEmpresa(e.codigo)}
                  className={`rounded-iris-sm border px-2 py-1 text-[11px] transition-colors ${
                    ativa
                      ? 'border-accent bg-accent-soft text-accent-text'
                      : 'border-line bg-surface text-tx-2 hover:bg-surface-2'
                  }`}
                  title={e.razaosocial}
                >
                  <span className="font-mono">{e.codigo}</span>
                  <span className="ml-1">{e.nomeFantasia || e.razaosocial}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Período usado + ações em massa */}
      {data && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-iris-lg border border-accent bg-accent-soft px-3 py-2 text-xs">
          <div className="text-accent-text">
            Histórico: <strong>{data.periodoHistorico}</strong> · Mês alvo: <strong>{nomeMes(anoMes)}</strong> · {empresasSel.length} empresa(s)
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={aplicarSugestaoTodos}
              className="rounded-iris-sm border border-accent bg-surface px-2 py-1 text-[11px] font-medium text-accent-text hover:bg-accent-soft transition-colors"
            >
              Restaurar sugestões
            </button>
            <button
              type="button"
              onClick={toggleTodos}
              disabled={elegives === 0}
              className="rounded-iris-sm border border-accent bg-surface px-2 py-1 text-[11px] font-medium text-accent-text hover:bg-accent-soft transition-colors disabled:opacity-50"
            >
              {todosElegSel ? 'Desmarcar todos' : 'Marcar elegíveis'}
            </button>
          </div>
        </div>
      )}

      {/* KPIs dos selecionados + distribuidor de meta total */}
      {data && (
        <div className="grid gap-2 md:grid-cols-[1fr_1fr_1fr_auto]">
          {/* KPI 1: soma das metas selecionadas */}
          <div className="rounded-iris-lg border border-accent bg-accent-soft p-2">
            <div className="text-[10px] uppercase tracking-wide text-accent-text">Soma das metas</div>
            <div className="mt-0.5 text-lg font-bold text-tx">{money(agregados.somaMetas)}</div>
            <div className="text-[10px] text-tx-3">{itensSelecionados.length} selecionada(s)</div>
          </div>

          {/* KPI 2: soma compra 3M (base do rateio) */}
          <div className="rounded-iris-lg border border-line bg-surface p-2">
            <div className="text-[10px] uppercase tracking-wide text-tx-3">Compra 3M dos selecionados</div>
            <div className="mt-0.5 text-lg font-bold text-tx">{money(agregados.somaCompra3M)}</div>
            <div className="text-[10px] text-tx-3">base do rateio proporcional</div>
          </div>

          {/* KPI 3: crescimento ponderado por volume */}
          <div className={`rounded-iris-lg border p-2 ${
            agregados.crescPonderado > 1
              ? 'border-ok-border bg-ok-soft'
              : agregados.crescPonderado < -1
              ? 'border-danger-border bg-danger-soft'
              : 'border-line bg-surface'
          }`}>
            <div className="text-[10px] uppercase tracking-wide text-tx-3">Crescimento vendas (pond.)</div>
            <div className={`mt-0.5 text-lg font-bold ${
              agregados.crescPonderado > 1 ? 'text-ok'
              : agregados.crescPonderado < -1 ? 'text-danger'
              : 'text-tx'
            }`}>
              {itensSelecionados.length === 0 ? '—' : (
                <>{agregados.crescPonderado > 0 ? '+' : ''}{agregados.crescPonderado.toFixed(1)}%</>
              )}
            </div>
            <div className="text-[10px] text-tx-3">ponderado por volume 3M</div>
          </div>

          {/* Distribuidor: meta total → rateia proporcional ao volume */}
          <div className="rounded-iris-lg border border-line bg-surface p-2 md:min-w-[260px]">
            <div className="text-[10px] uppercase tracking-wide text-tx-3">Meta total a distribuir</div>
            <div className="mt-0.5 flex items-center gap-1">
              <div className="flex-1">
                <InputMoeda value={metaTotal} onChange={setMetaTotal} />
              </div>
              <button
                type="button"
                onClick={distribuirMetaTotal}
                disabled={metaTotal == null || metaTotal <= 0 || itensSelecionados.length === 0}
                className="h-9 rounded-iris-sm bg-tx px-2 text-[11px] font-medium text-surface hover:opacity-90 transition-opacity disabled:opacity-50"
                title="Distribui o valor entre os selecionados proporcional ao volume 3M"
              >
                Distribuir
              </button>
            </div>
            <div className="mt-0.5 text-[10px] text-tx-3">rateio ∝ compra 3M</div>
          </div>
        </div>
      )}

      {/* Tabela */}
      <div className="flex-1 overflow-auto rounded-iris-lg border border-line bg-surface shadow-sm">
        {empresasSel.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 p-10 text-center">
            <ClipboardDocumentListIcon className="h-10 w-10 text-tx-3" />
            <p className="text-sm text-tx-3">Selecione ao menos uma empresa.</p>
          </div>
        ) : isLoading ? (
          <LoadingGrid dimensao={dimensao} limit={limit} />
        ) : items.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 p-10 text-center">
            <ClipboardDocumentListIcon className="h-10 w-10 text-tx-3" />
            <p className="text-sm text-tx-3">Sem histórico de compra nos últimos 3 meses para os filtros atuais.</p>
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="sticky top-0 z-10 bg-surface-2">
              <tr className="border-b border-line text-left text-tx-2">
                <th className="w-8 px-3 py-2 text-center">
                  <input
                    type="checkbox"
                    checked={todosElegSel}
                    onChange={toggleTodos}
                    disabled={elegives === 0}
                    title="Selecionar todos os elegíveis"
                    className="accent-accent"
                  />
                </th>
                <th className="px-3 py-2 font-medium">Código / Nome</th>
                <th className="px-3 py-2 text-right font-medium">Compra 3M</th>
                <th className="px-3 py-2 text-right font-medium">Média/mês</th>
                {dimensao !== 'fornecedor' && (
                  <th className="px-3 py-2 text-right font-medium">Vendas 3M</th>
                )}
                <th className="px-3 py-2 text-right font-medium">Crescimento</th>
                <th className="px-3 py-2 font-medium">Descrição</th>
                <th className="px-3 py-2 text-right font-medium">Meta (R$)</th>
                <th className="px-3 py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const linha = linhas[it.codigo];
                if (!linha) return null;
                const crescPos = it.crescimentoVendasPct > 1;
                const crescNeg = it.crescimentoVendasPct < -1;
                const linhaClasses = it.jaTemMeta
                  ? 'bg-surface-2 opacity-80'
                  : linha.selecionada
                  ? 'bg-accent-soft'
                  : 'hover:bg-surface-2';
                return (
                  <tr key={it.codigo} className={`border-b border-line last:border-0 ${linhaClasses}`}>
                    <td className="px-3 py-2 text-center">
                      <input
                        type="checkbox"
                        checked={linha.selecionada}
                        onChange={(e) => updateLinha(it.codigo, { selecionada: e.target.checked })}
                        disabled={it.jaTemMeta}
                        className="accent-accent"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <span className="font-mono text-[10px] text-tx-3">{it.codigo}</span>
                      <div className="text-tx">{it.nome}</div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right font-medium text-tx">
                      {money(it.compra3M)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right text-tx-2">
                      {money(it.mediaMensalCompra)}
                    </td>
                    {dimensao !== 'fornecedor' && (
                      <td className="whitespace-nowrap px-3 py-2 text-right text-tx-2">
                        {it.vendas3M > 0 ? money(it.vendas3M) : <span className="text-tx-3">—</span>}
                      </td>
                    )}
                    <td className="whitespace-nowrap px-3 py-2 text-right">
                      {it.vendas3M === 0 && dimensao !== 'fornecedor' ? (
                        <span className="text-tx-3">—</span>
                      ) : (
                        <span className={`inline-flex items-center gap-0.5 rounded-full px-2 text-[11px] font-semibold ${
                          crescPos ? 'bg-ok-soft text-ok'
                          : crescNeg ? 'bg-danger-soft text-danger'
                          : 'bg-surface-2 text-tx-2'
                        }`}>
                          {crescPos ? <ArrowTrendingUpIcon className="h-3 w-3" /> : crescNeg ? <ArrowTrendingDownIcon className="h-3 w-3" /> : null}
                          {it.crescimentoVendasPct > 0 ? '+' : ''}{it.crescimentoVendasPct.toFixed(1)}%
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        value={linha.descricao}
                        onChange={(e) => updateLinha(it.codigo, { descricao: e.target.value })}
                        disabled={it.jaTemMeta}
                        maxLength={120}
                        className="h-7 w-full rounded-iris-sm border border-line-strong bg-surface-2 px-1.5 text-xs text-tx placeholder:text-tx-3 focus:outline-none focus:bg-surface focus:border-accent focus:ring-[3px] focus:ring-accent-soft disabled:opacity-60"
                      />
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right">
                      <div className="inline-block w-32">
                        <InputMoeda
                          value={linha.valorMeta}
                          onChange={(v) => updateLinha(it.codigo, { valorMeta: v })}
                        />
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2">
                      {it.jaTemMeta ? (
                        <Link
                          to="/compras/metas/dimensao"
                          className="inline-flex items-center gap-1 rounded-full bg-ok-soft px-2 py-0.5 text-[11px] font-semibold text-ok hover:opacity-90 transition-opacity"
                          title={`Meta existente: ${money(it.valorMetaExistente ?? 0)}`}
                        >
                          <CheckBadgeIcon className="h-3 w-3" />
                          Meta ativa {money(it.valorMetaExistente ?? 0)}
                          <ChevronRightIcon className="h-3 w-3" />
                        </Link>
                      ) : (
                        <span className="text-[11px] text-tx-3">Disponível</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer com ação principal */}
      {data && podeEditar && (
        <div className="flex items-center justify-between gap-2 rounded-iris-lg border border-line bg-surface px-3 py-2 text-xs shadow-sm">
          <div className="text-tx-2">
            <strong className="text-tx">{itensSelecionados.length}</strong> selecionada(s)
            {itensSelecionados.length > 0 && (
              <> · Soma: <strong className="text-tx">{money(somaSelecionada)}</strong></>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate('/compras/metas/dimensao')}
              className={btnSecundario}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={salvar}
              disabled={itensSelecionados.length === 0 || criarLoteMutation.isPending}
              className={btnPrimario}
            >
              {criarLoteMutation.isPending
                ? 'Criando…'
                : `Criar ${itensSelecionados.length} meta${itensSelecionados.length === 1 ? '' : 's'}`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MetasCompraDimensaoLotePage;
